import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

const initialForm = {
  name: '',
  email: '',
  accountIdentifier: '',
  confirmation: false
};

const BeestmodeDeleteAccountPage = () => {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const updateField = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setForm((current) => ({ ...current, [target.name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);

    if (!form.email.trim() || !form.confirmation) {
      setResult({ type: 'error', message: 'Enter the email address for the Beestmode account and confirm your request.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'accountDeletionRequests'), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        accountIdentifier: form.accountIdentifier.trim(),
        confirmation: true,
        requestType: 'account_deletion',
        source: 'public_web',
        status: 'pending',
        submittedAt: serverTimestamp()
      });
      setForm(initialForm);
      setResult({ type: 'success', message: 'Your deletion request has been received. We will verify the account and contact you at the email address provided if we need more information.' });
    } catch (error) {
      console.error('Unable to submit account deletion request:', error);
      setResult({ type: 'error', message: 'We could not submit your request. Please try again, or email haggaimagen87@gmail.com with the subject “Beestmode account deletion”.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main style={styles.page}>
      <section style={styles.card} aria-labelledby="delete-account-title">
        <header style={styles.header}>
          <img src="/logo_shade_transparent.png" alt="Beestmode" style={styles.logo} />
          <p style={styles.eyebrow}>BEESTMODE</p>
          <h1 id="delete-account-title" style={styles.title}>Request account deletion</h1>
          <p style={styles.lead}>Use this page to request deletion of your Beestmode account without installing or signing in to the app.</p>
        </header>

        <section style={styles.section} aria-labelledby="what-is-deleted">
          <h2 id="what-is-deleted" style={styles.heading}>What we delete</h2>
          <p style={styles.text}>After we verify that you own the account, we delete your Beestmode account and the data associated with it, including your profile details, workout plans and routines, exercise and workout history, progress records, preferences, and app-generated account content.</p>
          <p style={styles.text}>We may retain limited records where required for legal, fraud-prevention, accounting, or subscription obligations. Deletion-request records are retained only as long as needed to process and document the request.</p>
        </section>

        <section style={styles.section} aria-labelledby="submit-request">
          <h2 id="submit-request" style={styles.heading}>Submit your request</h2>
          <p style={styles.text}>Provide the email address used for your Beestmode account. An account ID or username helps us locate the correct account, if you know it.</p>
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>
              Name <span style={styles.optional}>(optional)</span>
              <input style={styles.input} type="text" name="name" value={form.name} onChange={updateField} maxLength="100" autoComplete="name" disabled={isSubmitting} />
            </label>
            <label style={styles.label}>
              Beestmode account email
              <input style={styles.input} type="email" name="email" value={form.email} onChange={updateField} maxLength="254" autoComplete="email" required disabled={isSubmitting} />
            </label>
            <label style={styles.label}>
              Account ID or username <span style={styles.optional}>(optional)</span>
              <input style={styles.input} type="text" name="accountIdentifier" value={form.accountIdentifier} onChange={updateField} maxLength="128" disabled={isSubmitting} />
            </label>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" name="confirmation" checked={form.confirmation} onChange={updateField} required disabled={isSubmitting} />
              <span>I am requesting deletion of my Beestmode account and associated data.</span>
            </label>
            <button type="submit" style={styles.button} disabled={isSubmitting}>{isSubmitting ? 'Submitting request…' : 'Request account deletion'}</button>
          </form>
          {result && <p role="status" style={result.type === 'success' ? styles.success : styles.error}>{result.message}</p>}
        </section>

        <footer style={styles.footer}>
          <p style={styles.text}>You can also request deletion by emailing <a href="mailto:haggaimagen87@gmail.com?subject=Beestmode%20account%20deletion" style={styles.link}>haggaimagen87@gmail.com</a>.</p>
          <a href="/beestmodeprivacyen" style={styles.link}>Beestmode Privacy Policy</a>
        </footer>
      </section>
    </main>
  );
};

const styles = {
  page: { minHeight: '100vh', background: '#f4f7fb', padding: '32px 16px', boxSizing: 'border-box', color: '#1d2939', fontFamily: 'Arial, sans-serif', lineHeight: 1.55 },
  card: { maxWidth: '760px', margin: '0 auto', background: '#fff', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 10px 35px rgba(31, 41, 55, 0.12)' },
  header: { padding: '38px 32px 34px', textAlign: 'center', color: '#fff', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
  logo: { width: '72px', height: '72px', objectFit: 'contain', marginBottom: '8px' },
  eyebrow: { margin: 0, fontSize: '0.8rem', letterSpacing: '0.14em', fontWeight: 700, opacity: 0.84 },
  title: { margin: '6px 0 10px', fontSize: '2rem', lineHeight: 1.2 },
  lead: { margin: 0, maxWidth: '570px', marginLeft: 'auto', marginRight: 'auto', fontSize: '1.05rem' },
  section: { padding: '28px 32px', borderBottom: '1px solid #e5e7eb' },
  heading: { margin: '0 0 12px', fontSize: '1.35rem', color: '#312e81' },
  text: { margin: '0 0 12px' },
  form: { display: 'grid', gap: '17px', marginTop: '20px' },
  label: { display: 'grid', gap: '7px', fontWeight: 700, fontSize: '0.95rem' },
  optional: { color: '#667085', fontWeight: 400 },
  input: { width: '100%', padding: '12px', boxSizing: 'border-box', border: '1px solid #bfc6d4', borderRadius: '8px', fontSize: '1rem' },
  checkboxLabel: { display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '0.95rem' },
  button: { padding: '13px 18px', border: 0, borderRadius: '8px', color: '#fff', background: '#4f46e5', cursor: 'pointer', fontSize: '1rem', fontWeight: 700 },
  success: { margin: '20px 0 0', padding: '12px', borderRadius: '8px', background: '#ecfdf3', color: '#027a48' },
  error: { margin: '20px 0 0', padding: '12px', borderRadius: '8px', background: '#fef3f2', color: '#b42318' },
  footer: { padding: '24px 32px', background: '#fafafa' },
  link: { color: '#4338ca', fontWeight: 700 }
};

export default BeestmodeDeleteAccountPage;
